import {
  type ChangeEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import { Hash, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/base/input";
import { cn } from "@/lib/utils";

const client = generateClient<Schema>();

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface MerchantSelectorProps {
  /** Currently-selected businessId. Empty string = no selection. */
  value: string;
  /** Called when the user picks a result or pastes a valid UUID. */
  onChange: (businessId: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

interface MerchantStub {
  id: string;
  businessName: string;
}

// ── One-time paginated loader ──────────────────────────────────────────────
// Keeps a module-level in-flight promise so concurrent mounts/focus events
// never fire parallel paginations.
let loadAllPromise: Promise<MerchantStub[]> | null = null;

async function loadAllMerchants(): Promise<MerchantStub[]> {
  if (loadAllPromise) return loadAllPromise;

  loadAllPromise = (async () => {
    const all: MerchantStub[] = [];
    let cursor: string | undefined;
    do {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Amplify ModelFilter type is too deeply nested to construct type-safely; matches merchants_repository.ts pattern
      const listOpts: any = {
        limit: 500,
        nextToken: cursor,
        selectionSet: ["id", "businessName"],
      };
      const { data, nextToken, errors } =
        await client.models.BusinessProfile.list(listOpts);
      if (errors?.length) throw new Error(errors[0].message);
      all.push(
        ...(data ?? []).map(
          (b: { id: string; businessName?: string | null }) => ({
            id: b.id,
            businessName: b.businessName ?? "",
          }),
        ),
      );
      cursor = nextToken ?? undefined;
    } while (cursor);
    return all;
  })();

  // Clear the cached promise on failure so a retry is possible
  loadAllPromise.catch(() => {
    loadAllPromise = null;
  });

  return loadAllPromise;
}

export function MerchantSelector({
  value,
  onChange,
  placeholder = "Search by business name or paste ID",
  disabled = false,
}: MerchantSelectorProps) {
  const [query, setQuery] = useState("");
  const [allMerchants, setAllMerchants] = useState<MerchantStub[] | null>(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  // Track which value we've already resolved a name for to avoid redundant lookups
  const resolvedValueRef = useRef<string>("");

  // ── Outside click to close ─────────────────────────────────────────

  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setFocused(false);
      }
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  // ── Resolve display name from local cache when value changes ───────

  useEffect(() => {
    if (!value || resolvedValueRef.current === value) return;
    resolvedValueRef.current = value;

    if (allMerchants) {
      const found = allMerchants.find((m) => m.id === value);
      setSelectedName(found?.businessName ?? null);
    }
    // If allMerchants isn't loaded yet the focus handler will trigger the load;
    // once it resolves the effect will re-run via allMerchants state update.
  }, [value, allMerchants]);

  // ── Eager load on first focus ──────────────────────────────────────

  async function triggerLoad() {
    if (allMerchants !== null || loadingAll) return;
    setLoadingAll(true);
    setLoadError(null);
    try {
      const merchants = await loadAllMerchants();
      setAllMerchants(merchants);
      // Resolve selectedName if we have a pending value
      if (value) {
        const found = merchants.find((m) => m.id === value);
        setSelectedName(found?.businessName ?? null);
        resolvedValueRef.current = value;
      }
    } catch (err) {
      setLoadError(
        err instanceof Error ? err.message : "Failed to load merchants",
      );
    } finally {
      setLoadingAll(false);
    }
  }

  async function handleRetry() {
    loadAllPromise = null;
    setAllMerchants(null);
    await triggerLoad();
  }

  // ── Client-side filtering ──────────────────────────────────────────

  const trimmedQuery = query.trim();
  const isDirectUuid = UUID_REGEX.test(trimmedQuery);

  const filteredResults: MerchantStub[] =
    !isDirectUuid && trimmedQuery.length >= 1 && allMerchants
      ? allMerchants
          .filter((m) =>
            m.businessName.toLowerCase().includes(trimmedQuery.toLowerCase()),
          )
          .slice(0, 50)
      : [];

  // ── Dropdown visibility ─────────────────────────────────────────────

  const showDropdown =
    open &&
    (loadingAll ||
      !!loadError ||
      isDirectUuid ||
      filteredResults.length > 0 ||
      (trimmedQuery.length >= 1 && !loadingAll && !loadError));

  // ── Event handlers ──────────────────────────────────────────────────

  function handleSelect(merchant: MerchantStub) {
    setSelectedName(merchant.businessName);
    setQuery("");
    setOpen(false);
    resolvedValueRef.current = merchant.id;
    onChange(merchant.id);
  }

  function handleSelectDirectId(id: string) {
    setSelectedName(null);
    setQuery("");
    setOpen(false);
    resolvedValueRef.current = id;
    onChange(id);
  }

  function handleFocus() {
    setFocused(true);
    setOpen(true);
    if (value && !query) {
      setQuery(selectedName ?? value);
    }
    void triggerLoad();
  }

  function handleBlur() {
    // Delay so click on a result registers before blur closes the dropdown
    setTimeout(() => {
      setFocused(false);
      setQuery("");
    }, 150);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);
    // Clear selection when user starts typing a new query
    if (value && val !== selectedName) {
      setSelectedName(null);
      resolvedValueRef.current = "";
      onChange("");
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      setFocused(false);
      setQuery("");
    }
  }

  // Display value: show query text when focused, otherwise show name or raw ID
  const displayValue = focused ? query : (selectedName ?? (value || ""));

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        className="font-mono"
        value={displayValue}
        placeholder={placeholder}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label="Search merchant by name or paste business ID"
        aria-expanded={open}
        aria-autocomplete="list"
        role="combobox"
      />

      {showDropdown && (
        <ul
          role="listbox"
          className="border-border bg-card absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-md border shadow-lg"
        >
          {/* UUID escape hatch — show at top */}
          {isDirectUuid && (
            <li
              role="option"
              aria-selected={false}
              className="hover:bg-muted flex cursor-pointer items-center gap-2 px-3 py-2 text-sm"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectDirectId(trimmedQuery);
              }}
            >
              <Hash className="text-subtle-foreground h-3 w-3 shrink-0" />
              <span className="text-muted-foreground italic">
                Use as ID directly
              </span>
              <span className="text-subtle-foreground ml-auto font-mono text-xs">
                {trimmedQuery.slice(0, 8)}…
              </span>
            </li>
          )}

          {loadingAll && (
            <li className="text-muted-foreground flex items-center gap-2 px-3 py-3 text-sm">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading merchants…
            </li>
          )}

          {!loadingAll && loadError && (
            <li className="flex items-center justify-between px-3 py-2 text-sm text-red-600">
              <span>{loadError}</span>
              <button
                type="button"
                className="ml-2 text-xs underline hover:no-underline"
                onMouseDown={(e) => {
                  e.preventDefault();
                  void handleRetry();
                }}
              >
                Retry
              </button>
            </li>
          )}

          {!loadingAll &&
            !loadError &&
            !isDirectUuid &&
            filteredResults.length === 0 &&
            trimmedQuery.length >= 1 && (
              <li className="text-subtle-foreground px-3 py-2 text-sm">
                No merchants matching &ldquo;{trimmedQuery}&rdquo;
              </li>
            )}

          {filteredResults.map((merchant) => (
            <li
              key={merchant.id}
              role="option"
              aria-selected={merchant.id === value}
              className={cn(
                "hover:bg-muted cursor-pointer px-3 py-2",
                merchant.id === value && "bg-muted",
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(merchant);
              }}
            >
              <span className="text-foreground block text-sm font-medium">
                {merchant.businessName}
              </span>
              <span className="text-subtle-foreground block font-mono text-xs">
                {merchant.id.slice(0, 8)}…{merchant.id.slice(-4)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
